import { supabase } from "../utils/supabaseClient";
import { CustomTheme, ThemeApiResponse, ThemeProperties, UpdateThemeInput } from "../types/customTheme";

/**
 * Fetch all custom themes for a branch
 */
export const getCustomThemes = async (branchId: string): Promise<CustomTheme[]> => {
	try {
		const { data, error } = await (supabase.rpc as any)('get_branch_custom_theme', {
			p_payload: { branch_id: branchId }
		});

		if (error) {
			throw error;
		}

		const response = data as ThemeApiResponse;
		
		if (response?.success && Array.isArray(response.data)) {
			return response.data;
		}

		return [];
	} catch (err) {
		console.error("Error fetching custom themes:", err);
		throw err;
	}
};

/**
 * Create or update custom themes for a branch
 * - To create: pass theme without id
 * - To update: pass theme with id
 */
export const upsertCustomThemes = async (
	branchId: string,
	themes: (ThemeProperties | UpdateThemeInput)[]
): Promise<CustomTheme[]> => {
	try {
		const payload = {
			branch_id: branchId,
			themes: themes
		};

		const { data, error } = await (supabase.rpc as any)('upsert_branch_custom_theme', {
			p_payload: payload
		});

		if (error) {
			throw error;
		}

		const response = data as ThemeApiResponse;

		if (response?.success && Array.isArray(response.data)) {
			return response.data;
		}

		if (!response?.success) {
			throw new Error(response?.message || 'Failed to save themes');
		}

		return [];
	} catch (err) {
		console.error("Error upserting custom themes:", err);
		throw err;
	}
};

/**
 * Delete a custom theme by removing it from the themes array
 * This is done by upserting only the themes that should remain
 */
export const deleteCustomTheme = async (
	branchId: string,
	themeId: string,
	remainingThemes: CustomTheme[]
): Promise<CustomTheme[]> => {
	try {
		// Filter out the theme to delete and upsert the remaining
		const themesToKeep = remainingThemes
			.filter(t => t.id !== themeId)
			.map(t => ({ id: t.id, ...t.theme }));

		// If there are themes to keep, upsert them
		// If no themes remain, we need to handle this case
		const payload = {
			branch_id: branchId,
			themes: themesToKeep.length > 0 ? themesToKeep : []
		};

		const { data, error } = await (supabase.rpc as any)('upsert_branch_custom_theme', {
			p_payload: payload
		});

		if (error) {
			throw error;
		}

		const response = data as ThemeApiResponse;

		if (response?.success) {
			return response.data || [];
		}

		throw new Error(response?.message || 'Failed to delete theme');
	} catch (err) {
		console.error("Error deleting custom theme:", err);
		throw err;
	}
};
